/**
 * Admin / Manager / Accountant 共用佈局 — 對齊 ZHCB design tokens（v3）
 * 深色側邊欄 #0b0f1c，寬 240px
 * Logo「ZHcert 驗證管理」+ 副標籤「驗證營運平臺」
 *
 * 工作台類型對應：
 *   - admin:      完整系統設置 + 全功能
 *   - manager:    業務功能 + 部分系統設置（差異由能力過濾 + DB menuHidden 黑名單產生）
 *   - accountant: 聚焦報價／繳費管理（派案專區）
 *
 * Task 9：菜單來源改為 getMenuForWorkbench(workbenchType, user, user.menuHidden ?? [])，
 * 以真實用戶物件 + DB 黑名單動態過濾，取代原硬編封裝函數。
 */
import { useMemo } from 'react';
import {
  LayoutDashboard,
  Megaphone,
  Building2,
  Landmark,
  Send,
  ShieldCheck,
  ClipboardCheck,
  FlaskConical,
  TriangleAlert,
  Scale,
  FileCheck2,
  Stamp,
  SlidersHorizontal,
  ChartBar,
  Bell,
} from 'lucide-react';
import BaseLayout from '@/components/layout/BaseLayout';
import { getMenuForWorkbench } from '@/components/layout/menu-registry';
import type { BaseLayoutConfig, MenuItem } from '@/components/layout/layout-menu-config';
import { useAuthStore } from '@/lib/auth-context';
import { color, roleAccent } from '@/styles/tokens';

/**
 * Admin/Manager 菜單 icon 映射表
 * key 為菜單項的 key 值（路由路徑或分組 key）
 */
const ADMIN_ICON_MAP: Record<string, React.ReactNode> = {
  '/dashboard': <LayoutDashboard size={16} />,
  'bulletin-group': <Megaphone size={16} />,
  '/settings/clients': <Building2 size={16} />,
  '/certified-orgs': <Landmark size={16} />,
  '/dispatch': <Send size={16} />,
  '/bulletin': <Megaphone size={16} />,
  '/me/notifications': <Bell size={16} />,
  'audit-zone': <ShieldCheck size={16} />,
  '/audit-tasks': <ClipboardCheck size={16} />,
  '/organic-materials': <FlaskConical size={16} />,
  '/risk-assessment': <TriangleAlert size={16} />,
  '/decisions': <Scale size={16} />,
  '/certificates': <FileCheck2 size={16} />,
  '/tc': <Stamp size={16} />,
  'system-settings': <SlidersHorizontal size={16} />,
  'analytics-group': <ChartBar size={16} />,
};

/** 為菜單項注入 lucide icon（僅注入一級菜單） */
function injectAdminIcons(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    if (!item || !('key' in item)) return item;
    const icon = ADMIN_ICON_MAP[item.key as string];
    return icon ? { ...item, icon } : item;
  });
}

interface AdminLayoutProps {
  children: React.ReactNode;
  /**
   * 工作台類型，決定使用哪套菜單基準樹與副標題：
   * - admin      完整系統設置 + 全功能
   * - manager    業務功能 + 部分系統設置（差異由能力過濾 + DB menuHidden 黑名單產生）
   * - accountant（序52）聚焦報價／繳費管理（派案專區）
   */
  workbenchType?: 'admin' | 'manager' | 'accountant';
}

export default function AdminLayout({ children, workbenchType = 'admin' }: AdminLayoutProps) {
  /* 讀取真實用戶物件，用於能力過濾與 DB menuHidden 黑名單 */
  const user = useAuthStore((s) => s.user);

  const config: BaseLayoutConfig = useMemo(
    () => ({
      sider: {
        width: 240,
        background: color.sidebar.bg,
        className: 'sidebar-dark',
      },
      logo: {
        title: 'ZHcert 驗證管理',
        titleColor: '#ffffff',
        gradient: roleAccent.admin.gradient,
        subtitle: workbenchType === 'accountant' ? '報價／繳費管理' : '驗證管理平臺',
        subtitleColor: color.sidebar.textMuted,
      },
      /**
       * Task 9：改用 getMenuForWorkbench 傳入真實 user 物件與 DB menuHidden 黑名單，
       * 取代原 getAdminMenuItems/getManagerMenuItems/getAccountantMenuItems 靜態封裝。
       * - user?.capabilities：動態 RBAC 能力集，存在時比 roles 更準確。
       * - user?.menuHidden：DB role_menu_hidden 黑名單，由後端 /me 回傳。
       * - 兩者均為可選，未攜帶時 userCan 自動回落 roles → DEFAULT_ROLE_CAPABILITIES。
       */
      menuItems: injectAdminIcons(
        getMenuForWorkbench(
          workbenchType,
          user ? { capabilities: user.capabilities, roles: user.roles } : {},
          user?.menuHidden ?? [],
        ),
      ),
    }),
    [workbenchType, user],
  );

  return <BaseLayout config={config}>{children}</BaseLayout>;
}
